import { cn } from "@gymflow/helpers";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Button, EditIcon, TrashIcon } from "../..";

type ManageItemProps<TItem extends { id: number; name: string }> = {
  isEditing: boolean;
  onStartEdit: (item: TItem) => Promise<void> | void;
  item: TItem;
  onDeleteItem: (item: TItem) => Promise<void> | void;
};

export const ManageItem = <TItem extends { id: number; name: string }>({
  item,
  isEditing,
  onStartEdit,
  onDeleteItem,
}: ManageItemProps<TItem>) => {
  const { t } = useTranslation();

  const onClickDelete = useCallback(async () => {
    await onDeleteItem(item);
  }, [onDeleteItem, item]);

  const onClickEdit = useCallback(async () => {
    onStartEdit(item);
  }, [onStartEdit, item]);

  return (
    <div className="flex flex-row items-center justify-between border-b border-b-gray-300 last:border-none">
      <div
        className={cn("text-base font-medium text-gray-950", {
          "text-gray-500 py-2.5": isEditing,
        })}
      >
        {item.name}
      </div>
      <div className="flex flex-row gap-1">
        {isEditing ? (
          <div className="text-base font-medium text-gray-500">
            {t("components.manageModal.manageItem.editing")}
          </div>
        ) : (
          <>
            <Button
              intent="transparent"
              className="p-2"
              onClick={async () => await onClickEdit()}
            >
              <EditIcon className="size-6" pathClassName="stroke-gray-500" />
            </Button>
            <Button
              intent="transparent"
              className="p-2"
              onClick={async () => await onClickDelete()}
            >
              <TrashIcon className="size-6" pathClassName="stroke-error-600" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
